import React, { useEffect, useRef, useState } from 'react'
import { Button, ButtonGroup, Form, Modal } from 'react-bootstrap'

import cogoToast from 'cogo-toast'
import moment from 'moment'

import AdjustersService from '../../../../services/AdjustersService'
import CasesService from '../../../../services/CasesService'
import format from '../../../../services/format'
import ProviderService from '../../../../services/ProviderService'
import ReferralSourcesService from '../../../../services/ReferralSourcesService'
import ActivityExpenseSelector from '../../../ActivityExpenseSelector'
import SmartAddressInput from '../../../common/SmartAddressInput'
import DatePicker from '../../../controls/DatePicker'
import Select from '../../../controls/Select'
import TextArea from '../../../controls/TextArea'
import FAIcon from '../../../FAIcon'
import ProviderAutocomplete from '../../../ProviderAutocomplete'
import ProviderForm from '../../../ProviderForm'

export default function WYSIWYGEditModal({
  show,
  onHide,
  title,
  type,
  value,
  onSave,
  caseModel,
  fieldKey,
  focusField,
  caseNumber,
  reportNumber,
  onRefresh,
}) {
  const descriptionInputRef = useRef(null)
  const [textVal, setTextVal] = useState('')
  const [isGeneratingNarrative, setIsGeneratingNarrative] = useState(false)
  const isGeneratingNarrativeRef = useRef(false)
  const [previousNarrative, setPreviousNarrative] = useState(null)
  const [canUndoNarrative, setCanUndoNarrative] = useState(false)
  const [dateVal, setDateVal] = useState(null)
  const [referralTypeVal, setReferralTypeVal] = useState('')

  // Address state
  const [addressVal, setAddressVal] = useState({
    street: '',
    city: '',
    state: '',
    zip: '',
  })
  // Relational selects state
  const [adjusterVal, setAdjusterVal] = useState('')
  const [adjusterOptions, setAdjusterOptions] = useState([])
  const [companyVal, setCompanyVal] = useState('')
  const [companyOptions, setCompanyOptions] = useState([])

  // Diagnosis state
  const [diagVal, setDiagVal] = useState({
    name: '',
    physician: '',
    date: null,
  })
  // Work Status state
  const [workStatusVal, setWorkStatusVal] = useState({
    date: null,
    treatmentProvider: '',
    status: '',
  })

  // MMI state
  const [mmiVal, setMmiVal] = useState({
    date: null,
    isActual: false,
    narrative: '',
  })

  // Employer state
  const [employerVal, setEmployerVal] = useState({
    name: '',
    address: { street: '', city: '', state: '', zip: '' },
    phone: '',
  })

  // Attorney state
  const [attorneyVal, setAttorneyVal] = useState({
    name: '',
    address: { street: '', city: '', state: '', zip: '' },
    phone: '',
  })

  // Claimant Name & Report Number state
  const [clientNameVal, setClientNameVal] = useState({ firstName: '', lastName: '' })
  const [reportNumberVal, setReportNumberVal] = useState('')

  // Surgery Dates state
  const [surgeryDatesVal, setSurgeryDatesVal] = useState([])

  // Diagnoses List state
  const [diagnosesListVal, setDiagnosesListVal] = useState([])

  // Injury Descriptions List state
  const [injuryDescriptionsListVal, setInjuryDescriptionsListVal] = useState([])

  // Review of Systems & Functional Status states
  const [systemsReviewVal, setSystemsReviewVal] = useState({
    neurological: '',
    cardiac: '',
    pulmonary: '',
    giGu: '',
    musculoskeletal: '',
    skin: '',
  })
  const [functionalStatusesVal, setFunctionalStatusesVal] = useState({
    cognition: '',
    communication: '',
    psychological: '',
    adls: '',
    limitations: '',
    sleep: '',
    pain: '',
    other: '',
  })

  // Providers list state
  const [providersVal, setProvidersVal] = useState([])
  const [selectedEditProviderId, setSelectedEditProviderId] = useState(null)
  const [addingClassification, setAddingClassification] = useState('Individual')
  const [draggedProviderIndex, setDraggedProviderIndex] = useState(null)
  const [dropTargetProviderIndex, setDropTargetProviderIndex] = useState(null)

  // Allergies & Medications list states
  const [allergiesListVal, setAllergiesListVal] = useState([])
  const [medicationsListVal, setMedicationsListVal] = useState([])

  // Activity state
  const [activityVal, setActivityVal] = useState({
    date: null,
    type: '',
    time: 0,
    description: '',
  })

  // Expense state
  const [expenseVal, setExpenseVal] = useState({
    date: null,
    type: '',
    amount: 0,
    distance: 0,
    description: '',
  })

  // Appointment Details state
  const [appointmentVal, setAppointmentVal] = useState({
    appointmentNumber: null,
    title: '',
    date: null,
    provider: '',
    description: '',
    note: '',
  })

  const [isDeletingConfirm, setIsDeletingConfirm] = useState(false)

  useEffect(() => {
    if (!show) return
    setIsDeletingConfirm(false)
    setIsGeneratingNarrative(false)
    setCanUndoNarrative(false)
    setPreviousNarrative(null)

    if (type === 'rich-text' || type === 'text') {
      setTextVal(value || '')
    } else if (type === 'date') {
      setDateVal(value ? moment.utc(value).format('YYYY-MM-DD') : null)
    } else if (type === 'address') {
      setAddressVal({
        street: value?.line1 || value?.street || '',
        city: value?.city || '',
        state: value?.state || '',
        zip: value?.zip || '',
      })
    } else if (type === 'work-status') {
      const history = caseModel?.status?.history || []
      const latest = history[history.length - 1] || caseModel?.status || {}
      setWorkStatusVal({
        date: latest.date ? moment.utc(latest.date).format('YYYY-MM-DD') : null,
        treatmentProvider: latest.treatmentProvider || '',
        status: latest.workStatus || latest.status || '',
      })
    } else if (type === 'client-name') {
      setClientNameVal({
        firstName: value?.firstName || '',
        lastName: value?.lastName || '',
      })
    } else if (type === 'report-number') {
      setReportNumberVal(value || '')
    } else if (type === 'referral-type') {
      setReferralTypeVal(value || '')
    } else if (type === 'mmi') {
      const history = caseModel?.mmi?.history || []
      const latest = history[history.length - 1] || {}
      setMmiVal({
        date: latest.date ? moment.utc(latest.date).format('YYYY-MM-DD') : null,
        isActual: !!latest.isActual,
        narrative: latest.narrative || '',
      })
    } else if (type === 'employer') {
      setEmployerVal({
        name: value?.name || '',
        address: {
          street: value?.address?.line1 || value?.address?.street || '',
          city: value?.address?.city || '',
          state: value?.address?.state || '',
          zip: value?.address?.zip || '',
        },
        phone: value?.contact?.phone || value?.phone || '',
      })
    } else if (type === 'attorney') {
      setAttorneyVal({
        name: value?.name || '',
        address: {
          street: value?.address?.line1 || value?.address?.street || '',
          city: value?.address?.city || '',
          state: value?.address?.state || '',
          zip: value?.address?.zip || '',
        },
        phone: value?.contact?.phone || value?.phone || '',
      })
    } else if (type === 'diagnosis') {
      const first = (caseModel?.diagnosis || [])[0] || {}
      setDiagVal({
        name: first.name || '',
        physician: first.physician || '',
        date: first.date ? moment.utc(first.date).format('YYYY-MM-DD') : null,
      })
    } else if (type === 'library-adjuster') {
      setAdjusterVal(value || '')
      AdjustersService.getAll().then(res => {
        setAdjusterOptions((res || []).map(a => ({ value: a.id, label: a.name, original: a })))
      })
    } else if (type === 'library-company') {
      setCompanyVal(value?.name || '')
      ReferralSourcesService.getAll().then(res => {
        setCompanyOptions((res || []).map(c => ({ value: c.name, label: c.name, original: c })))
      })
    } else if (type === 'surgery-dates') {
      setSurgeryDatesVal(
        (value || []).map(s => ({
          id: s.id,
          dateOfSurgery: s.dateOfSurgery ? moment.utc(s.dateOfSurgery).format('YYYY-MM-DD') : null,
          title: s.title || '',
          provider: s.provider || '',
        })),
      )
    } else if (type === 'diagnoses-list') {
      setDiagnosesListVal(
        (value || []).map(d => ({
          name: d.name || '',
          physician: d.physician || '',
          date: d.date ? moment.utc(d.date).format('YYYY-MM-DD') : null,
        })),
      )
    } else if (type === 'injury-descriptions-list') {
      const rawList = Array.isArray(value)
        ? value
        : typeof value === 'string' && value.trim()
        ? [{ description: value, provider: '', date: null }]
        : []
      setInjuryDescriptionsListVal(
        rawList.map(item => ({
          description: item?.description || '',
          provider: item?.provider || '',
          date: item?.date ? moment.utc(item.date).format('YYYY-MM-DD') : null,
        })),
      )
    } else if (type === 'allergies-list') {
      setAllergiesListVal(
        (value || []).map(a => ({
          name: a.name || (typeof a === 'string' ? a : ''),
          reaction: a.reaction || '',
        })),
      )
    } else if (type === 'medications-list') {
      setMedicationsListVal(
        (value || []).map(m => ({
          name: m.name || (typeof m === 'string' ? m : ''),
          dosage: m.dosage || '',
          frequency: m.frequency || '',
          reason: m.reason || '',
          dateStarted: m.dateStarted ? moment.utc(m.dateStarted).format('YYYY-MM-DD') : null,
          dateDiscontinued: m.dateDiscontinued ? moment.utc(m.dateDiscontinued).format('YYYY-MM-DD') : null,
          pharmacy: m.pharmacy || '',
          relatedToInjury: Boolean(m.relatedToInjury),
          preInjury: Boolean(m.preInjury || m.isPreInjury),
          isPreInjury: Boolean(m.preInjury || m.isPreInjury),
          discontinued: Boolean(m.discontinued || m.isDiscontinued || m.dateDiscontinued),
          isDiscontinued: Boolean(m.discontinued || m.isDiscontinued || m.dateDiscontinued),
        })),
      )
    } else if (type === 'systems-review') {
      setSystemsReviewVal({
        neurological: value?.neurological || '',
        cardiac: value?.cardiac || '',
        pulmonary: value?.pulmonary || '',
        giGu: value?.giGu || '',
        musculoskeletal: value?.musculoskeletal || '',
        skin: value?.skin || '',
      })
    } else if (type === 'functional-statuses') {
      setFunctionalStatusesVal({
        cognition: value?.cognition || '',
        communication: value?.communication || '',
        psychological: value?.psychological || '',
        adls: value?.adls || '',
        limitations: value?.limitations || '',
        sleep: value?.sleep || '',
        pain: value?.pain || '',
        other: value?.other || '',
      })
    } else if (type === 'activity') {
      setActivityVal({
        date: value?.date ? moment.utc(value.date).format('YYYY-MM-DD') : null,
        type: value?.type || '',
        time: value?.time || 0,
        description: value?.description || '',
      })
    } else if (type === 'expense') {
      setExpenseVal({
        date: value?.date ? moment.utc(value.date).format('YYYY-MM-DD') : null,
        type: value?.type || '',
        amount: value?.amount || 0,
        distance: value?.distance || 0,
        description: value?.description || '',
      })
    } else if (type === 'appointment-details' && value) {
      setAppointmentVal({
        appointmentNumber: value.appointmentNumber || (fieldKey ? parseInt(fieldKey.split('.')[1], 10) : null),
        title: value.title || value.eventTitle || '',
        date: value.startUtc
          ? moment(value.startUtc).format('YYYY-MM-DD')
          : value.date
          ? moment(value.date).format('YYYY-MM-DD')
          : null,
        provider: value.provider || value.individualProvider || '',
        description: value.description || '',
        note: value.note || '',
      })
    }
  }, [show, type, value, caseModel, fieldKey])

  useEffect(() => {
    if (!show) return
    if (type === 'treatment-providers' || type === 'transportation-list' || type === 'translation-list') {
      setProvidersVal(value || [])
    }
  }, [show, type, value, fieldKey])

  const handleGenerateNarrative = async e => {
    if (e) {
      e.preventDefault()
    }
    if (isGeneratingNarrative || isGeneratingNarrativeRef.current) return
    if (!caseNumber || !reportNumber) {
      cogoToast.error('Case number and report number are required to generate narrative.')
      return
    }
    isGeneratingNarrativeRef.current = true
    const currentText = textVal || ''
    setPreviousNarrative(currentText)
    setIsGeneratingNarrative(true)
    try {
      const res = await CasesService.draftAppointmentsNarrative(caseNumber, reportNumber)
      if (res && res.narrative) {
        setTextVal(res.narrative)
        const hasPrev = Boolean(currentText && currentText.trim() && currentText !== '<p></p>')
        setCanUndoNarrative(hasPrev)
        cogoToast.success('Narrative generated successfully.')
      } else {
        cogoToast.error('No narrative was generated.')
      }
    } catch (err) {
      cogoToast.error(err?.message || 'Failed to generate narrative.')
    } finally {
      setIsGeneratingNarrative(false)
      isGeneratingNarrativeRef.current = false
    }
  }

  const handleUndoNarrative = e => {
    if (e) {
      e.preventDefault()
    }
    if (previousNarrative !== null) {
      setTextVal(previousNarrative)
      setCanUndoNarrative(false)
      cogoToast.info('Restored previous narrative.')
    }
  }

  const handleSave = e => {
    if (e) {
      e.preventDefault()
      if (e.type === 'click') return
    }
    if (type === 'rich-text' || type === 'text') {
      onSave(textVal)
    } else if (type === 'date') {
      onSave(dateVal ? moment.utc(dateVal).startOf('day').format() : null)
    } else if (type === 'address') {
      onSave(addressVal)
    } else if (type === 'work-status') {
      onSave(workStatusVal)
    } else if (type === 'client-name') {
      onSave(clientNameVal)
    } else if (type === 'report-number') {
      onSave(reportNumberVal)
    } else if (type === 'referral-type') {
      onSave(referralTypeVal)
    } else if (type === 'mmi') {
      onSave(mmiVal)
    } else if (type === 'employer') {
      onSave(employerVal)
    } else if (type === 'attorney') {
      onSave(attorneyVal)
    } else if (type === 'diagnosis') {
      onSave(diagVal)
    } else if (type === 'library-adjuster') {
      onSave(adjusterVal)
    } else if (type === 'library-company') {
      const selectedOpt = companyOptions.find(x => x.value === companyVal)
      onSave(selectedOpt ? selectedOpt.original : null)
    } else if (type === 'surgery-dates') {
      onSave(surgeryDatesVal)
    } else if (type === 'diagnoses-list') {
      onSave(diagnosesListVal)
    } else if (type === 'injury-descriptions-list') {
      onSave(
        injuryDescriptionsListVal.filter(
          item =>
            (item.description && item.description.replace(/<[^>]*>/g, '').trim()) ||
            (item.provider && item.provider.trim()) ||
            item.date,
        ),
      )
    } else if (type === 'allergies-list') {
      onSave(allergiesListVal.filter(a => a.name && a.name.trim()))
    } else if (type === 'medications-list') {
      onSave(medicationsListVal.filter(m => m.name && m.name.trim()))
    } else if (type === 'systems-review') {
      onSave(systemsReviewVal)
    } else if (type === 'functional-statuses') {
      onSave(functionalStatusesVal)
    } else if (type === 'treatment-providers' || type === 'transportation-list' || type === 'translation-list') {
      onSave(providersVal)
    } else if (type === 'activity') {
      if (!activityVal.type || !activityVal.description || !activityVal.date || activityVal.time <= 0) {
        cogoToast.error(
          'Date, Type, Time (Hours), and Description are required. If you want to remove this activity, please click the Delete button.',
        )
        return
      }
      onSave(activityVal)
    } else if (type === 'expense') {
      const isMileage = expenseVal.type === 'Mileage' || expenseVal.type === 'Travel'
      if (
        !expenseVal.type ||
        !expenseVal.date ||
        !expenseVal.description ||
        (isMileage && expenseVal.distance <= 0) ||
        (!isMileage && expenseVal.amount <= 0)
      ) {
        cogoToast.error(
          'Date, Type, Description, and Amount/Distance are required. If you want to remove this expense, please click the Delete button.',
        )
        return
      }
      onSave(expenseVal)
    } else if (type === 'appointment-details') {
      if (!appointmentVal.title || !appointmentVal.title.trim()) {
        cogoToast.error('Event Title is required.')
        return
      }
      onSave(appointmentVal)
    }
  }

  const renderEditor = () => {
    switch (type) {
      case 'client-name':
        return (
          <div className="form-row">
            <div className="col-md-6 form-group">
              <Form.Label>First Name</Form.Label>
              <Form.Control
                type="text"
                value={clientNameVal.firstName}
                onChange={e => setClientNameVal({ ...clientNameVal, firstName: e.target.value })}
              />
            </div>
            <div className="col-md-6 form-group">
              <Form.Label>Last Name</Form.Label>
              <Form.Control
                type="text"
                value={clientNameVal.lastName}
                onChange={e => setClientNameVal({ ...clientNameVal, lastName: e.target.value })}
              />
            </div>
          </div>
        )
      case 'report-number':
        return (
          <Form.Group>
            <Form.Label>Progress Report Number</Form.Label>
            <Form.Control type="number" value={reportNumberVal} onChange={e => setReportNumberVal(e.target.value)} />
          </Form.Group>
        )
      case 'referral-type':
        return (
          <Form.Group>
            <Form.Label>Type of Referral</Form.Label>
            <Form.Control
              as="select"
              id="wysiwyg-referral-type"
              value={referralTypeVal}
              onChange={e => setReferralTypeVal(e.target.value)}
            >
              <option value="">-- Select Type --</option>
              <option value="FCM">FCM (Field Case Management)</option>
              <option value="TCM">TCM (Telephonic Case Management)</option>
              <option value="Task">Task</option>
            </Form.Control>
          </Form.Group>
        )
      case 'rich-text': {
        const isCurrentMedicalStatus = fieldKey === 'report.currentMedicalStatus'
        return (
          <div>
            {isCurrentMedicalStatus && canUndoNarrative && (
              <div className="alert alert-info py-2 px-3 mb-2 d-flex justify-content-between align-items-center">
                <span className="small">Previous narrative was overwritten by generation.</span>
                <Button
                  variant="outline-dark"
                  size="sm"
                  onMouseDown={handleUndoNarrative}
                  onClick={handleUndoNarrative}
                >
                  Undo (Restore Previous)
                </Button>
              </div>
            )}
            {isCurrentMedicalStatus && isGeneratingNarrative ? (
              <div className="text-center py-5 border rounded bg-light">
                <div className="spinner-border text-primary" role="status">
                  <span className="sr-only">Generating narrative...</span>
                </div>
                <div className="mt-3 font-weight-bold text-muted">
                  Generating appointment narrative via Bedrock AI...
                </div>
                <div className="small text-muted">
                  Synthesizing clinical findings and treatment notes for completed appointments.
                </div>
              </div>
            ) : (
              <TextArea
                className="form-control p-0 border-0"
                areaProps={{
                  value: textVal,
                  onChange: val => setTextVal(val),
                }}
              />
            )}
          </div>
        )
      }
      case 'text':
        return <Form.Control type="text" value={textVal} onChange={e => setTextVal(e.target.value)} />
      case 'date':
        return <DatePicker value={dateVal} onChange={e => setDateVal(e.target.value)} autoComplete="off" />
      case 'address':
        return (
          <div>
            <SmartAddressInput value={addressVal} onChange={newAddr => setAddressVal(newAddr)} />
          </div>
        )
      case 'work-status': {
        const providerOptions = (caseModel?.treatmentProviders || []).map(p => ({
          value: p.name,
          label: p.name,
        }))
        return (
          <div>
            <Form.Group>
              <Form.Label>Date Reported</Form.Label>
              <DatePicker
                value={workStatusVal.date}
                onChange={e => setWorkStatusVal({ ...workStatusVal, date: e.target.value })}
                autoComplete="off"
              />
            </Form.Group>
            <Form.Group className="mt-3">
              <Form.Label>Treatment Provider</Form.Label>
              <Select
                value={providerOptions.find(x => x.value === workStatusVal.treatmentProvider) || null}
                options={providerOptions}
                onChange={e => setWorkStatusVal({ ...workStatusVal, treatmentProvider: e ? e.target.value : '' })}
                placeholder="Select provider"
                isClearable
              />
            </Form.Group>
            <Form.Group className="mt-3">
              <Form.Label>Work Status</Form.Label>
              <Form.Control
                type="text"
                value={workStatusVal.status}
                onChange={e => setWorkStatusVal({ ...workStatusVal, status: e.target.value })}
                placeholder="Enter work status (e.g. Regular Duty, Off Work)"
              />
            </Form.Group>
          </div>
        )
      }
      case 'mmi':
        return (
          <div>
            <Form.Group>
              <Form.Label>MMI Date</Form.Label>
              <DatePicker
                value={mmiVal.date}
                onChange={e => setMmiVal({ ...mmiVal, date: e.target.value })}
                autoComplete="off"
              />
            </Form.Group>
            <Form.Group className="mt-3 d-flex align-items-center">
              <Form.Check
                type="checkbox"
                id="isActualMmiCheckbox"
                label="Is Actual MMI (rather than Estimated)"
                checked={mmiVal.isActual}
                onChange={e => setMmiVal({ ...mmiVal, isActual: e.target.checked })}
              />
            </Form.Group>
            <Form.Group className="mt-3">
              <Form.Label>Narrative / Notes</Form.Label>
              <TextArea
                className="form-control p-0 border-0"
                areaProps={{
                  value: mmiVal.narrative,
                  onChange: val => setMmiVal({ ...mmiVal, narrative: val }),
                }}
              />
            </Form.Group>
          </div>
        )
      case 'employer':
        return (
          <div>
            <Form.Group>
              <Form.Label>Employer Name</Form.Label>
              <Form.Control
                type="text"
                value={employerVal.name}
                onChange={e => setEmployerVal({ ...employerVal, name: e.target.value })}
                placeholder="Enter employer name..."
              />
            </Form.Group>
            <Form.Group className="mt-3">
              <Form.Label>Employer Address</Form.Label>
              <SmartAddressInput
                value={employerVal.address}
                onChange={newAddr => setEmployerVal({ ...employerVal, address: newAddr })}
              />
            </Form.Group>
            <Form.Group className="mt-3">
              <Form.Label>Employer Phone</Form.Label>
              <Form.Control
                type="text"
                value={employerVal.phone}
                onChange={e => setEmployerVal({ ...employerVal, phone: e.target.value })}
                placeholder="Enter employer phone number..."
              />
            </Form.Group>
          </div>
        )
      case 'attorney':
        return (
          <div>
            <Form.Group>
              <Form.Label>Attorney / Firm Name</Form.Label>
              <Form.Control
                type="text"
                value={attorneyVal.name}
                onChange={e => setAttorneyVal({ ...attorneyVal, name: e.target.value })}
                placeholder="Enter attorney or firm name..."
              />
            </Form.Group>
            <Form.Group className="mt-3">
              <Form.Label>Address</Form.Label>
              <SmartAddressInput
                value={attorneyVal.address}
                onChange={newAddr => setAttorneyVal({ ...attorneyVal, address: newAddr })}
              />
            </Form.Group>
            <Form.Group className="mt-3">
              <Form.Label>Phone</Form.Label>
              <Form.Control
                type="text"
                value={attorneyVal.phone}
                onChange={e => setAttorneyVal({ ...attorneyVal, phone: e.target.value })}
                placeholder="Enter phone number..."
              />
            </Form.Group>
          </div>
        )
      case 'diagnosis': {
        const providerOptions = (caseModel?.treatmentProviders || [])
          .map(p => {
            const name =
              p.classification === 'Individual'
                ? `${p.firstName || ''} ${p.lastName || ''}${p.suffix ? `, ${p.suffix}` : ''}`.trim()
                : p.name || p.organizationName || ''
            return { value: name, label: name }
          })
          .filter(p => !!p.value)

        if (diagVal.physician && !providerOptions.some(o => o.value === diagVal.physician)) {
          providerOptions.unshift({ value: diagVal.physician, label: diagVal.physician })
        }

        return (
          <div>
            <Form.Group>
              <Form.Label>Diagnosis Name / Description</Form.Label>
              <Form.Control
                type="text"
                value={diagVal.name}
                onChange={e => setDiagVal({ ...diagVal, name: e.target.value })}
                placeholder="Enter diagnosis description..."
              />
            </Form.Group>
            <Form.Group className="mt-3">
              <Form.Label>Physician</Form.Label>
              <Select
                value={
                  providerOptions.find(x => x.value === diagVal.physician) ||
                  (diagVal.physician ? { value: diagVal.physician, label: diagVal.physician } : null)
                }
                options={providerOptions}
                onChange={e => setDiagVal({ ...diagVal, physician: e ? e.target.value : '' })}
                placeholder="Select physician from case providers..."
                isClearable
                allowFreeType
              />
            </Form.Group>
            <Form.Group className="mt-3">
              <Form.Label>Diagnosis Date</Form.Label>
              <DatePicker
                value={diagVal.date}
                onChange={e => setDiagVal({ ...diagVal, date: e.target.value })}
                autoComplete="off"
              />
            </Form.Group>
          </div>
        )
      }
      case 'library-adjuster':
        return (
          <Form.Group style={{ minHeight: '120px' }}>
            <Form.Label>Select Adjuster</Form.Label>
            <Select
              value={adjusterOptions.find(x => x.value === adjusterVal) || null}
              options={adjusterOptions}
              onChange={e => setAdjusterVal(e ? e.target.value : '')}
              placeholder="Select adjuster..."
              isClearable
            />
          </Form.Group>
        )
      case 'library-company':
        return (
          <Form.Group style={{ minHeight: '120px' }}>
            <Form.Label>Select Company / Entity</Form.Label>
            <Select
              value={companyOptions.find(x => x.value === companyVal) || null}
              options={companyOptions}
              onChange={e => setCompanyVal(e ? e.target.value : '')}
              placeholder="Select company..."
              isClearable
            />
          </Form.Group>
        )
      case 'surgery-dates':
        return (
          <div>
            <Form.Label>Surgery Date(s)</Form.Label>
            {surgeryDatesVal.map((s, idx) => (
              <div key={idx} className="d-flex align-items-center mb-2">
                <div style={{ flex: 1 }}>
                  <DatePicker
                    value={s.dateOfSurgery}
                    onChange={e => {
                      const updated = [...surgeryDatesVal]
                      updated[idx].dateOfSurgery = e.target.value
                      setSurgeryDatesVal(updated)
                    }}
                    autoComplete="off"
                  />
                </div>
                <Button
                  variant="outline-danger"
                  className="ml-2"
                  onMouseDown={e => {
                    e.preventDefault()
                    setSurgeryDatesVal(surgeryDatesVal.filter((_, i) => i !== idx))
                  }}
                >
                  Remove
                </Button>
              </div>
            ))}
            <Button
              variant="outline-primary"
              className="mt-2"
              onMouseDown={e => {
                e.preventDefault()
                setSurgeryDatesVal([
                  ...surgeryDatesVal,
                  { id: null, dateOfSurgery: null, title: 'Surgery', provider: '' },
                ])
              }}
            >
              + Add Surgery Date
            </Button>
          </div>
        )
      case 'diagnoses-list': {
        const baseProviderOptions = (caseModel?.treatmentProviders || [])
          .map(p => {
            const name =
              p.classification === 'Individual'
                ? `${p.firstName || ''} ${p.lastName || ''}${p.suffix ? `, ${p.suffix}` : ''}`.trim()
                : p.name || p.organizationName || ''
            return { value: name, label: name }
          })
          .filter(p => !!p.value)

        return (
          <div>
            {diagnosesListVal.map((d, idx) => {
              const itemOptions = [...baseProviderOptions]
              if (d.physician && !itemOptions.some(o => o.value === d.physician)) {
                itemOptions.unshift({ value: d.physician, label: d.physician })
              }
              return (
                <div key={idx} className="border p-3 mb-3 rounded position-relative">
                  <Button
                    variant="outline-danger"
                    size="sm"
                    className="position-absolute"
                    style={{ top: '10px', right: '10px' }}
                    onMouseDown={e => {
                      e.preventDefault()
                      setDiagnosesListVal(diagnosesListVal.filter((_, i) => i !== idx))
                    }}
                  >
                    Remove
                  </Button>
                  <Form.Group>
                    <Form.Label>Diagnosis Description/Name</Form.Label>
                    <Form.Control
                      type="text"
                      value={d.name}
                      onChange={e => {
                        const updated = [...diagnosesListVal]
                        updated[idx].name = e.target.value
                        setDiagnosesListVal(updated)
                      }}
                      placeholder="Enter diagnosis name..."
                    />
                  </Form.Group>
                  <Form.Group className="mt-2">
                    <Form.Label>Physician</Form.Label>
                    <Select
                      value={
                        itemOptions.find(x => x.value === d.physician) ||
                        (d.physician ? { value: d.physician, label: d.physician } : null)
                      }
                      options={itemOptions}
                      onChange={e => {
                        const updated = [...diagnosesListVal]
                        updated[idx].physician = e ? e.target.value : ''
                        setDiagnosesListVal(updated)
                      }}
                      placeholder="Select physician from case providers..."
                      isClearable
                      allowFreeType
                    />
                  </Form.Group>
                  <Form.Group className="mt-2">
                    <Form.Label>Date</Form.Label>
                    <DatePicker
                      value={d.date}
                      onChange={e => {
                        const updated = [...diagnosesListVal]
                        updated[idx].date = e.target.value
                        setDiagnosesListVal(updated)
                      }}
                      autoComplete="off"
                    />
                  </Form.Group>
                </div>
              )
            })}
            <Button
              variant="outline-primary"
              onMouseDown={e => {
                e.preventDefault()
                setDiagnosesListVal([...diagnosesListVal, { name: '', physician: '', date: null }])
              }}
            >
              + Add Diagnosis
            </Button>
          </div>
        )
      }
      case 'injury-descriptions-list': {
        return (
          <div>
            {injuryDescriptionsListVal.map((item, idx) => (
              <div key={idx} className="border p-3 mb-3 rounded position-relative">
                <Button
                  variant="outline-danger"
                  size="sm"
                  className="position-absolute"
                  style={{ top: '10px', right: '10px', zIndex: 1 }}
                  onMouseDown={e => {
                    e.preventDefault()
                    setInjuryDescriptionsListVal(injuryDescriptionsListVal.filter((_, i) => i !== idx))
                  }}
                >
                  Remove
                </Button>
                <Form.Group>
                  <Form.Label>Description of Injury</Form.Label>
                  <TextArea
                    className="form-control p-0 border-0"
                    areaProps={{
                      value: item.description,
                      onChange: val => {
                        const updated = [...injuryDescriptionsListVal]
                        updated[idx] = { ...updated[idx], description: val }
                        setInjuryDescriptionsListVal(updated)
                      },
                    }}
                  />
                </Form.Group>
                <Form.Group className="mt-2">
                  <Form.Label>Provided By</Form.Label>
                  <Form.Control
                    type="text"
                    value={item.provider}
                    onChange={e => {
                      const updated = [...injuryDescriptionsListVal]
                      updated[idx] = { ...updated[idx], provider: e.target.value }
                      setInjuryDescriptionsListVal(updated)
                    }}
                    placeholder="Enter name and title..."
                  />
                </Form.Group>
                <Form.Group className="mt-2">
                  <Form.Label>Date</Form.Label>
                  <DatePicker
                    value={item.date}
                    onChange={e => {
                      const updated = [...injuryDescriptionsListVal]
                      updated[idx] = { ...updated[idx], date: e.target.value }
                      setInjuryDescriptionsListVal(updated)
                    }}
                    autoComplete="off"
                  />
                </Form.Group>
              </div>
            ))}
            <Button
              variant="outline-primary"
              onMouseDown={e => {
                e.preventDefault()
                setInjuryDescriptionsListVal([
                  ...injuryDescriptionsListVal,
                  { description: '', provider: '', date: null },
                ])
              }}
            >
              + Add Injury Description
            </Button>
          </div>
        )
      }
      case 'allergies-list': {
        return (
          <div>
            {allergiesListVal.map((a, idx) => (
              <div key={idx} className="border p-3 mb-3 rounded position-relative">
                <Button
                  variant="outline-danger"
                  size="sm"
                  className="position-absolute"
                  style={{ top: '10px', right: '10px' }}
                  onMouseDown={e => {
                    e.preventDefault()
                    setAllergiesListVal(allergiesListVal.filter((_, i) => i !== idx))
                  }}
                >
                  Remove
                </Button>
                <Form.Group>
                  <Form.Label>Allergy Name / Allergen</Form.Label>
                  <Form.Control
                    type="text"
                    value={a.name || ''}
                    onChange={e => {
                      const updated = [...allergiesListVal]
                      updated[idx].name = e.target.value
                      setAllergiesListVal(updated)
                    }}
                    placeholder="Enter allergen name (e.g. Amoxicillin, Penicillin)..."
                  />
                </Form.Group>
                <Form.Group className="mt-2">
                  <Form.Label>Documented Reaction (Optional)</Form.Label>
                  <Form.Control
                    type="text"
                    value={a.reaction || ''}
                    onChange={e => {
                      const updated = [...allergiesListVal]
                      updated[idx].reaction = e.target.value
                      setAllergiesListVal(updated)
                    }}
                    placeholder="Enter allergic reaction (e.g. Hives, Anaphylaxis, Rash)..."
                  />
                </Form.Group>
              </div>
            ))}
            <Button
              variant="outline-primary"
              onMouseDown={e => {
                e.preventDefault()
                setAllergiesListVal([...allergiesListVal, { name: '', reaction: '' }])
              }}
            >
              + Add Allergy
            </Button>
          </div>
        )
      }
      case 'medications-list': {
        return (
          <div>
            {medicationsListVal.map((m, idx) => (
              <div key={idx} className="border p-3 mb-3 rounded position-relative">
                <Button
                  variant="outline-danger"
                  size="sm"
                  className="position-absolute"
                  style={{ top: '10px', right: '10px' }}
                  onMouseDown={e => {
                    e.preventDefault()
                    setMedicationsListVal(medicationsListVal.filter((_, i) => i !== idx))
                  }}
                >
                  Remove
                </Button>
                <Form.Group>
                  <Form.Label>Medication Name</Form.Label>
                  <Form.Control
                    type="text"
                    value={m.name || ''}
                    onChange={e => {
                      const updated = [...medicationsListVal]
                      updated[idx].name = e.target.value
                      setMedicationsListVal(updated)
                    }}
                    placeholder="Enter medication name (e.g. Ibuprofen, Gabapentin)..."
                  />
                </Form.Group>
                <div className="row mt-2">
                  <div className="col-md-6">
                    <Form.Group>
                      <Form.Label>Dosage / Strength</Form.Label>
                      <Form.Control
                        type="text"
                        value={m.dosage || ''}
                        onChange={e => {
                          const updated = [...medicationsListVal]
                          updated[idx].dosage = e.target.value
                          setMedicationsListVal(updated)
                        }}
                        placeholder="e.g. 800 mg, 10 mg"
                      />
                    </Form.Group>
                  </div>
                  <div className="col-md-6">
                    <Form.Group>
                      <Form.Label>Frequency</Form.Label>
                      <Form.Control
                        type="text"
                        value={m.frequency || ''}
                        onChange={e => {
                          const updated = [...medicationsListVal]
                          updated[idx].frequency = e.target.value
                          setMedicationsListVal(updated)
                        }}
                        placeholder="e.g. TID, daily, PRN"
                      />
                    </Form.Group>
                  </div>
                </div>
                <Form.Group className="mt-2">
                  <Form.Label>Reason / Indication</Form.Label>
                  <Form.Control
                    type="text"
                    value={m.reason || ''}
                    onChange={e => {
                      const updated = [...medicationsListVal]
                      updated[idx].reason = e.target.value
                      setMedicationsListVal(updated)
                    }}
                    placeholder="e.g. Pain, inflammation, sleep"
                  />
                </Form.Group>
                <div className="d-flex flex-wrap align-items-center mt-2" style={{ gap: '1.5rem' }}>
                  <Form.Check
                    type="checkbox"
                    id={`med-relatedToInjury-${idx}`}
                    label="Related to injury"
                    checked={Boolean(m.relatedToInjury)}
                    onChange={e => {
                      const updated = [...medicationsListVal]
                      updated[idx].relatedToInjury = e.target.checked
                      setMedicationsListVal(updated)
                    }}
                  />
                  <Form.Check
                    type="checkbox"
                    id={`med-preInjury-${idx}`}
                    label="Pre-injury"
                    checked={Boolean(m.preInjury || m.isPreInjury)}
                    onChange={e => {
                      const updated = [...medicationsListVal]
                      updated[idx].preInjury = e.target.checked
                      updated[idx].isPreInjury = e.target.checked
                      setMedicationsListVal(updated)
                    }}
                  />
                  <Form.Check
                    type="checkbox"
                    id={`med-discontinued-${idx}`}
                    label="Discontinued"
                    checked={Boolean(m.discontinued || m.isDiscontinued)}
                    onChange={e => {
                      const updated = [...medicationsListVal]
                      updated[idx].discontinued = e.target.checked
                      updated[idx].isDiscontinued = e.target.checked
                      setMedicationsListVal(updated)
                    }}
                  />
                </div>
              </div>
            ))}
            <Button
              variant="outline-primary"
              onMouseDown={e => {
                e.preventDefault()
                setMedicationsListVal([
                  ...medicationsListVal,
                  {
                    name: '',
                    dosage: '',
                    frequency: '',
                    reason: '',
                    relatedToInjury: false,
                    preInjury: false,
                    isPreInjury: false,
                    discontinued: false,
                    isDiscontinued: false,
                  },
                ])
              }}
            >
              + Add Medication
            </Button>
          </div>
        )
      }
      case 'systems-review':
        return (
          <div>
            <Form.Group className="mb-2">
              <Form.Label>Neurological</Form.Label>
              <TextArea
                className="form-control p-0 border-0"
                areaProps={{
                  value: systemsReviewVal.neurological,
                  onChange: val => setSystemsReviewVal({ ...systemsReviewVal, neurological: val }),
                }}
              />
            </Form.Group>
            <Form.Group className="mb-2">
              <Form.Label>Cardiac</Form.Label>
              <TextArea
                className="form-control p-0 border-0"
                areaProps={{
                  value: systemsReviewVal.cardiac,
                  onChange: val => setSystemsReviewVal({ ...systemsReviewVal, cardiac: val }),
                }}
              />
            </Form.Group>
            <Form.Group className="mb-2">
              <Form.Label>Pulmonary</Form.Label>
              <TextArea
                className="form-control p-0 border-0"
                areaProps={{
                  value: systemsReviewVal.pulmonary,
                  onChange: val => setSystemsReviewVal({ ...systemsReviewVal, pulmonary: val }),
                }}
              />
            </Form.Group>
            <Form.Group className="mb-2">
              <Form.Label>GI/GU</Form.Label>
              <TextArea
                className="form-control p-0 border-0"
                areaProps={{
                  value: systemsReviewVal.giGu,
                  onChange: val => setSystemsReviewVal({ ...systemsReviewVal, giGu: val }),
                }}
              />
            </Form.Group>
            <Form.Group className="mb-2">
              <Form.Label>Musculoskeletal</Form.Label>
              <TextArea
                className="form-control p-0 border-0"
                areaProps={{
                  value: systemsReviewVal.musculoskeletal,
                  onChange: val => setSystemsReviewVal({ ...systemsReviewVal, musculoskeletal: val }),
                }}
              />
            </Form.Group>
            <Form.Group className="mb-2">
              <Form.Label>Skin-integumentary</Form.Label>
              <TextArea
                className="form-control p-0 border-0"
                areaProps={{
                  value: systemsReviewVal.skin,
                  onChange: val => setSystemsReviewVal({ ...systemsReviewVal, skin: val }),
                }}
              />
            </Form.Group>
          </div>
        )
      case 'functional-statuses':
        return (
          <div>
            <Form.Group className="mb-2">
              <Form.Label>Cognition</Form.Label>
              <TextArea
                className="form-control p-0 border-0"
                areaProps={{
                  value: functionalStatusesVal.cognition,
                  onChange: val => setFunctionalStatusesVal({ ...functionalStatusesVal, cognition: val }),
                }}
              />
            </Form.Group>
            <Form.Group className="mb-2">
              <Form.Label>Communication</Form.Label>
              <TextArea
                className="form-control p-0 border-0"
                areaProps={{
                  value: functionalStatusesVal.communication,
                  onChange: val => setFunctionalStatusesVal({ ...functionalStatusesVal, communication: val }),
                }}
              />
            </Form.Group>
            <Form.Group className="mb-2">
              <Form.Label>Psychological / Behavior Issues</Form.Label>
              <TextArea
                className="form-control p-0 border-0"
                areaProps={{
                  value: functionalStatusesVal.psychological,
                  onChange: val => setFunctionalStatusesVal({ ...functionalStatusesVal, psychological: val }),
                }}
              />
            </Form.Group>
            <Form.Group className="mb-2">
              <Form.Label>Activities of Daily living (ADLS)/ Self-care</Form.Label>
              <TextArea
                className="form-control p-0 border-0"
                areaProps={{
                  value: functionalStatusesVal.adls,
                  onChange: val => setFunctionalStatusesVal({ ...functionalStatusesVal, adls: val }),
                }}
              />
            </Form.Group>
            <Form.Group className="mb-2">
              <Form.Label>Limitations of Activities due to injury/Level of Independence</Form.Label>
              <TextArea
                className="form-control p-0 border-0"
                areaProps={{
                  value: functionalStatusesVal.limitations,
                  onChange: val => setFunctionalStatusesVal({ ...functionalStatusesVal, limitations: val }),
                }}
              />
            </Form.Group>
            <Form.Group className="mb-2">
              <Form.Label>Sleep</Form.Label>
              <TextArea
                className="form-control p-0 border-0"
                areaProps={{
                  value: functionalStatusesVal.sleep,
                  onChange: val => setFunctionalStatusesVal({ ...functionalStatusesVal, sleep: val }),
                }}
              />
            </Form.Group>
            <Form.Group className="mb-2">
              <Form.Label>Pain levels</Form.Label>
              <TextArea
                className="form-control p-0 border-0"
                areaProps={{
                  value: functionalStatusesVal.pain,
                  onChange: val => setFunctionalStatusesVal({ ...functionalStatusesVal, pain: val }),
                }}
              />
            </Form.Group>
            <Form.Group className="mb-2">
              <Form.Label>Other</Form.Label>
              <TextArea
                className="form-control p-0 border-0"
                areaProps={{
                  value: functionalStatusesVal.other,
                  onChange: val => setFunctionalStatusesVal({ ...functionalStatusesVal, other: val }),
                }}
              />
            </Form.Group>
          </div>
        )
      case 'treatment-providers':
        return (
          <div>
            <div className="d-flex justify-content-between align-items-center mb-2">
              <div className="font-weight-bold">Associated Case Providers:</div>
              <span className="text-muted small">
                <FAIcon name="grip-vertical" className="mr-1" />
                Drag a clinician onto an organization to affiliate, or click × to unlink
              </span>
            </div>
            {providersVal.length > 0 ? (
              <div className="list-group mb-3">
                {providersVal.map((prov, idx) => {
                  const rawName = (prov.name || '').trim()
                  const hasPipeSplit = rawName.includes(' | ')
                  const baseNameWithoutOrg = hasPipeSplit ? rawName.split(' | ')[0].trim() : rawName
                  const pipeOrgName = hasPipeSplit ? rawName.split(' | ').slice(1).join(' | ').trim() : null

                  const suffixStr = prov.suffix && !baseNameWithoutOrg.includes(prov.suffix) ? `, ${prov.suffix}` : ''
                  const specStr =
                    prov.specialties &&
                    prov.specialties !== 'Treatment' &&
                    prov.specialties !== 'Treatment Provider' &&
                    !baseNameWithoutOrg.includes(prov.specialties)
                      ? `, ${prov.specialties}`
                      : prov.speciality &&
                        prov.speciality !== prov.type &&
                        prov.speciality !== 'Treatment' &&
                        prov.speciality !== 'Treatment Provider' &&
                        !baseNameWithoutOrg.includes(prov.speciality)
                      ? `, ${prov.speciality}`
                      : ''

                  const baseProviderName = `${baseNameWithoutOrg}${suffixStr}${specStr}`

                  const effectiveOrg =
                    pipeOrgName ||
                    prov.parentOrganization ||
                    (prov.companyName && prov.companyName.trim().toLowerCase() !== baseNameWithoutOrg.toLowerCase()
                      ? prov.companyName.trim()
                      : null)

                  const explicitClassification = (prov.classification || '').trim().toLowerCase()
                  const hasClinicianCredentials =
                    /\b(MD|DO|NP|PA|PA-C|PT|DPT|DC|RN|PhD|PsyD|LCSW|DPM|DDS|DMD|OD|FAAN|FACS)\b/i.test(
                      baseProviderName,
                    ) || /^Dr\.?\s/i.test(baseProviderName)
                  const hasFacilityKeyword =
                    /\b(Center|Clinic|Hospital|Medical Group|Associates|Health|Imaging|Radiology|Diagnostics|Pharmacy|Physical Therapy|Rehab|Rehabilitation|Orthopedics|Institute|Facility|Surgery|Surgical|Care|Services|Foundation|Network|System|Labs|Laboratory|Urgent Care|LLC|Inc|PC|Corp)\b/i.test(
                      baseNameWithoutOrg,
                    )

                  const isIndividualRow =
                    explicitClassification === 'individual' ||
                    Boolean(effectiveOrg) ||
                    (explicitClassification !== 'organization' &&
                      (hasClinicianCredentials || Boolean(prov.firstName || prov.lastName) || !hasFacilityKeyword))

                  const targetOrgName = !isIndividualRow ? baseNameWithoutOrg : effectiveOrg
                  const canReceiveAffiliationDrop =
                    draggedProviderIndex !== null && draggedProviderIndex !== idx && Boolean(targetOrgName)
                  const isDropHovered = canReceiveAffiliationDrop && dropTargetProviderIndex === idx

                  return (
                    <div
                      key={prov.id || idx}
                      data-testid={`report-provider-row-${idx}`}
                      draggable={isIndividualRow}
                      onDragStart={e => {
                        if (!isIndividualRow) return
                        setDraggedProviderIndex(idx)
                        if (e.dataTransfer) {
                          e.dataTransfer.effectAllowed = 'move'
                          try {
                            e.dataTransfer.setData('text/plain', String(idx))
                          } catch (_err) {
                            // ignore in test environments
                          }
                        }
                      }}
                      onDragEnd={() => {
                        setDraggedProviderIndex(null)
                        setDropTargetProviderIndex(null)
                      }}
                      onDragOver={e => {
                        if (!canReceiveAffiliationDrop) return
                        e.preventDefault()
                        if (dropTargetProviderIndex !== idx) {
                          setDropTargetProviderIndex(idx)
                        }
                      }}
                      onDragLeave={() => {
                        if (dropTargetProviderIndex === idx) {
                          setDropTargetProviderIndex(null)
                        }
                      }}
                      onDrop={e => {
                        if (!canReceiveAffiliationDrop) return
                        e.preventDefault()
                        const sourceIdx = draggedProviderIndex
                        setDraggedProviderIndex(null)
                        setDropTargetProviderIndex(null)
                        if (sourceIdx === null || sourceIdx === idx) return
                        setProvidersVal(prev =>
                          prev.map((item, i) => {
                            if (i !== sourceIdx) return item
                            const cleanSourceName = (item.name || '').split(' | ')[0].trim()
                            return {
                              ...item,
                              _ownAddress: item._ownAddress !== undefined ? item._ownAddress : item.address || null,
                              _ownPhone: item._ownPhone !== undefined ? item._ownPhone : item.phone || '',
                              _ownFax: item._ownFax !== undefined ? item._ownFax : item.fax || '',
                              _ownEmail: item._ownEmail !== undefined ? item._ownEmail : item.email || '',
                              name: `${cleanSourceName} | ${targetOrgName}`,
                              parentOrganization: targetOrgName,
                              parentProviderId: prov.id || prov.providerId || null,
                              companyName: targetOrgName,
                              address: prov.address || item.address || null,
                              phone: prov.phone || item.phone || '',
                              fax: prov.fax || item.fax || '',
                              email: prov.email || item.email || '',
                              _affiliationAction: 'affiliate',
                            }
                          }),
                        )
                      }}
                      style={{
                        border: isDropHovered
                          ? '2px dashed #2563eb'
                          : canReceiveAffiliationDrop
                          ? '1px dashed #93c5fd'
                          : undefined,
                        backgroundColor: isDropHovered
                          ? '#eff6ff'
                          : draggedProviderIndex === idx
                          ? '#f8fafc'
                          : undefined,
                        transition: 'all 0.15s ease',
                      }}
                      className="list-group-item d-flex justify-content-between align-items-center"
                    >
                      <div>
                        <div className="font-weight-bold d-flex align-items-center flex-wrap" style={{ gap: '4px' }}>
                          {isIndividualRow && (
                            <span
                              data-testid={`report-provider-drag-${idx}`}
                              title={
                                effectiveOrg
                                  ? 'Drag onto another Organization to reassign affiliation'
                                  : 'Drag onto an Organization to affiliate'
                              }
                              style={{
                                cursor: 'grab',
                                color: '#64748b',
                                marginRight: '6px',
                                userSelect: 'none',
                                display: 'inline-flex',
                                alignItems: 'center',
                              }}
                            >
                              <FAIcon name="grip-vertical" className="fa-sm" />
                            </span>
                          )}
                          <span>{baseProviderName}</span>
                          {effectiveOrg && (
                            <>
                              <span className="text-muted mx-1">|</span>
                              <span
                                className="d-inline-flex align-items-center"
                                style={{
                                  backgroundColor: '#f1f5f9',
                                  border: '1px solid #cbd5e1',
                                  borderRadius: '4px',
                                  padding: '0 6px',
                                  fontSize: '0.92em',
                                }}
                              >
                                <span>{effectiveOrg}</span>
                                <button
                                  type="button"
                                  data-testid={`report-provider-unlink-${idx}`}
                                  title={`Unlink ${baseProviderName} from ${effectiveOrg}`}
                                  onClick={async e => {
                                    e.preventDefault()
                                    e.stopPropagation()
                                    const targetId = prov.id || prov.providerId
                                    setProvidersVal(prev =>
                                      prev.map((item, i) =>
                                        i === idx
                                          ? {
                                              ...item,
                                              name: baseNameWithoutOrg,
                                              parentOrganization: null,
                                              parentProviderId: null,
                                              companyName: null,
                                              address: item._ownAddress !== undefined ? item._ownAddress : item.address,
                                              phone: item._ownPhone !== undefined ? item._ownPhone : item.phone,
                                              fax: item._ownFax !== undefined ? item._ownFax : item.fax,
                                              email: item._ownEmail !== undefined ? item._ownEmail : item.email,
                                              _affiliationAction: 'unlink',
                                            }
                                          : item,
                                      ),
                                    )
                                    if (targetId) {
                                      try {
                                        const fullProv = await ProviderService.get(targetId)
                                        if (fullProv) {
                                          const ownAddr =
                                            fullProv.addresses?.find(
                                              a => a.type === 'PrimaryPractice' || a.isPrimary,
                                            ) ||
                                            fullProv.addresses?.find(a => a.type === 'Mailing') ||
                                            fullProv.addresses?.[0] ||
                                            null
                                          setProvidersVal(prev =>
                                            prev.map((item, i) =>
                                              i === idx
                                                ? {
                                                    ...item,
                                                    address: ownAddr,
                                                    phone: fullProv.phone || '',
                                                    fax: fullProv.fax || '',
                                                    email: fullProv.email || '',
                                                  }
                                                : item,
                                            ),
                                          )
                                        }
                                      } catch {
                                        // Ignore lookup failure in offline/mock environments
                                      }
                                    } else {
                                      const rawLookupName = baseNameWithoutOrg
                                        .split(',')[0]
                                        .replace(/^(Dr\.?|MD|DO|DC|PT|DPT|NP|PA-C|PA|OD|PhD)\s+/i, '')
                                        .trim()
                                      if (rawLookupName) {
                                        try {
                                          const searchRes = await ProviderService.search({
                                            searchText: rawLookupName,
                                            classification: 'Individual',
                                            page: 1,
                                            take: 10,
                                          })
                                          const list = Array.isArray(searchRes) ? searchRes : searchRes?.results || []
                                          const match =
                                            list.find(
                                              p => p.name && p.name.toLowerCase() === rawLookupName.toLowerCase(),
                                            ) || list[0]
                                          if (match && match.id) {
                                            const fullProv = await ProviderService.get(match.id)
                                            if (fullProv) {
                                              const ownAddr =
                                                fullProv.addresses?.find(
                                                  a => a.type === 'PrimaryPractice' || a.isPrimary,
                                                ) ||
                                                fullProv.addresses?.find(a => a.type === 'Mailing') ||
                                                fullProv.addresses?.[0] ||
                                                null
                                              setProvidersVal(prev =>
                                                prev.map((item, i) =>
                                                  i === idx
                                                    ? {
                                                        ...item,
                                                        id: fullProv.id,
                                                        address: ownAddr,
                                                        phone: fullProv.phone || '',
                                                        fax: fullProv.fax || '',
                                                        email: fullProv.email || '',
                                                      }
                                                    : item,
                                                ),
                                              )
                                            }
                                          }
                                        } catch {
                                          // Ignore search fallback error
                                        }
                                      }
                                    }
                                  }}
                                  style={{
                                    border: 'none',
                                    background: 'transparent',
                                    color: '#64748b',
                                    fontWeight: 'bold',
                                    fontSize: '14px',
                                    lineHeight: 1,
                                    marginLeft: '5px',
                                    padding: '0 2px',
                                    cursor: 'pointer',
                                  }}
                                >
                                  ×
                                </button>
                              </span>
                            </>
                          )}
                          {isDropHovered && (
                            <span className="badge badge-primary ml-2" style={{ fontSize: '0.72rem', fontWeight: 600 }}>
                              Drop to affiliate with {targetOrgName}
                            </span>
                          )}
                        </div>
                        {prov.address && (
                          <div className="text-muted fa-sm">
                            {typeof prov.address === 'string' ? prov.address : format.address(prov.address)}
                          </div>
                        )}
                        {(prov.phone || prov.email || prov.fax) && (
                          <div className="text-muted fa-sm">
                            {[
                              prov.phone && `Ph: ${format.phone(prov.phone)}`,
                              prov.email && `Email: ${prov.email}`,
                              prov.fax && `Fax: ${format.phone(prov.fax)}`,
                            ]
                              .filter(Boolean)
                              .join(' | ')}
                          </div>
                        )}
                      </div>
                      <div className="d-flex align-items-center">
                        <Button
                          variant="outline-primary"
                          size="sm"
                          className="mr-1"
                          title="Edit"
                          aria-label="Edit"
                          disabled={!prov.id && !prov.providerId && !prov.name}
                          onClick={async e => {
                            e.preventDefault()
                            const candidateId = prov.id || prov.providerId
                            if (candidateId) {
                              try {
                                const existing = await ProviderService.get(candidateId)
                                if (existing && existing.id) {
                                  setSelectedEditProviderId(existing.id)
                                  return
                                }
                              } catch {
                                // Candidate ID is obsolete (e.g. merged duplicate); resolve below
                              }
                            }

                            const rawLookupName = (prov.name || prov.companyName || '')
                              .split('|')[0]
                              .split(',')[0]
                              .replace(/^(Dr\.?|MD|DO|DC|PT|DPT|NP|PA-C|PA|OD|PhD)\s+/i, '')
                              .trim()
                            if (rawLookupName) {
                              try {
                                const searchRes = await ProviderService.search({
                                  searchText: rawLookupName,
                                  page: 1,
                                  take: 20,
                                })
                                const list = Array.isArray(searchRes) ? searchRes : searchRes?.results || []
                                const match =
                                  list.find(
                                    item =>
                                      (item.name && item.name.toLowerCase() === rawLookupName.toLowerCase()) ||
                                      (item.organization &&
                                        item.organization.toLowerCase() === rawLookupName.toLowerCase()),
                                  ) || list[0]
                                if (match && match.id) {
                                  setProvidersVal(prev =>
                                    prev.map((p, i) => (i === idx ? { ...p, id: match.id, providerId: match.id } : p)),
                                  )
                                  setSelectedEditProviderId(match.id)
                                  return
                                }
                              } catch {
                                // Ignore search fallback error
                              }
                            }
                            cogoToast.warn(
                              'This provider record was merged or removed. Save or refresh the report to update the list.',
                            )
                          }}
                        >
                          <i className="fas fa-pencil-alt" />
                        </Button>
                        <Button
                          variant="outline-danger"
                          size="sm"
                          title="Remove"
                          aria-label="Remove"
                          onClick={e => {
                            e.preventDefault()
                            setProvidersVal(providersVal.filter((_, pIdx) => pIdx !== idx))
                          }}
                        >
                          <i className="fas fa-trash" />
                        </Button>
                      </div>
                    </div>
                  )
                })}
              </div>
            ) : (
              <div className="alert alert-warning mb-3">No providers associated with this case.</div>
            )}

            <Form.Group className="mb-2">
              <div className="d-flex justify-content-between align-items-center flex-wrap mb-2">
                <Form.Label className="font-weight-bold mb-0">Link Provider from Library:</Form.Label>
                <ButtonGroup size="sm" aria-label="Filter by Individual or Organization">
                  <Button
                    type="button"
                    variant={addingClassification === 'Individual' ? 'primary' : 'outline-secondary'}
                    onClick={() => setAddingClassification('Individual')}
                  >
                    <span role="img" aria-label="individual" className="mr-1">
                      👤
                    </span>
                    Individual
                  </Button>
                  <Button
                    type="button"
                    variant={addingClassification === 'Organization' ? 'primary' : 'outline-secondary'}
                    onClick={() => setAddingClassification('Organization')}
                  >
                    <span role="img" aria-label="organization" className="mr-1">
                      🏢
                    </span>
                    Organization
                  </Button>
                </ButtonGroup>
              </div>
              <ProviderAutocomplete
                onChange={() => {}}
                filter={{ classification: addingClassification }}
                onProviderSelect={selectedProvider => {
                  if (
                    selectedProvider &&
                    !providersVal.some(
                      p =>
                        (p.id && p.id === selectedProvider.id) ||
                        p.name === (selectedProvider.name || selectedProvider.organization),
                    )
                  ) {
                    const newProv = {
                      id: selectedProvider.id,
                      name: selectedProvider.name || selectedProvider.organization,
                      suffix: selectedProvider.suffix || '',
                      specialties: selectedProvider.specialties || '',
                      parentOrganization: selectedProvider.isIndividual ? selectedProvider.organization : '',
                      address: selectedProvider.address ? { line1: selectedProvider.address } : {},
                      phone: selectedProvider.phone || '',
                      email: selectedProvider.email || '',
                      fax: selectedProvider.fax || '',
                    }
                    setProvidersVal([...providersVal, newProv])
                  }
                }}
                placeholder={
                  addingClassification === 'Organization'
                    ? 'Search for an Organization by name or NPI...'
                    : 'Search for an Individual Provider by name or NPI...'
                }
              />
            </Form.Group>
          </div>
        )
      case 'transportation-list':
      case 'translation-list': {
        const autocompType = type === 'transportation-list' ? 'Transportation' : 'Translation'
        return (
          <div>
            <div className="mb-3 font-weight-bold">
              Associated {type === 'transportation-list' ? 'Transportation' : 'Translation'} Services:
            </div>
            {providersVal.length > 0 ? (
              <div className="list-group mb-3">
                {providersVal.map((prov, idx) => (
                  <div
                    key={prov.providerId || idx}
                    className="list-group-item d-flex justify-content-between align-items-center"
                  >
                    <div>
                      <div className="font-weight-bold">{prov.providerName || prov.name}</div>
                    </div>
                    <Button
                      variant="outline-danger"
                      size="sm"
                      onClick={e => {
                        e.preventDefault()
                        setProvidersVal(providersVal.filter((_, pIdx) => pIdx !== idx))
                      }}
                    >
                      <i className="fas fa-trash mr-1" /> Remove
                    </Button>
                  </div>
                ))}
              </div>
            ) : (
              <div className="alert alert-warning mb-3">No services associated with this case.</div>
            )}

            <Form.Group className="mb-2">
              <Form.Label className="font-weight-bold">Link Service from Library:</Form.Label>
              <ProviderAutocomplete
                type={autocompType}
                onChange={() => {}}
                onProviderSelect={selectedProvider => {
                  if (selectedProvider && !providersVal.some(p => p.providerId === selectedProvider.id)) {
                    const newProv = {
                      providerId: selectedProvider.id,
                      providerName: selectedProvider.name || selectedProvider.organization,
                    }
                    setProvidersVal([...providersVal, newProv])
                  }
                }}
                placeholder={`Search for ${autocompType} Providers...`}
              />
            </Form.Group>
          </div>
        )
      }
      case 'activity':
        return (
          <div>
            <Form.Group className="mb-2">
              <Form.Label>Date</Form.Label>
              <DatePicker
                value={activityVal.date}
                onChange={e => setActivityVal({ ...activityVal, date: e.target.value })}
                autoComplete="off"
              />
            </Form.Group>
            <Form.Group className="mb-2">
              <Form.Label>Type of Activity</Form.Label>
              <ActivityExpenseSelector
                value={activityVal.type}
                type="activity"
                onChange={e => setActivityVal({ ...activityVal, type: e.target.value })}
              />
            </Form.Group>
            <Form.Group className="mb-2">
              <Form.Label>Time (Hours)</Form.Label>
              <Form.Control
                type="number"
                step="0.1"
                value={activityVal.time}
                onChange={e => setActivityVal({ ...activityVal, time: parseFloat(e.target.value) || 0 })}
              />
            </Form.Group>
            <Form.Group className="mb-2">
              <Form.Label>Description</Form.Label>
              <Form.Control
                ref={descriptionInputRef}
                as="textarea"
                rows={3}
                value={activityVal.description}
                onChange={e => setActivityVal({ ...activityVal, description: e.target.value })}
              />
            </Form.Group>
          </div>
        )
      case 'expense': {
        const isMileage = expenseVal.type === 'Mileage' || expenseVal.type === 'Travel'
        return (
          <div>
            <Form.Group className="mb-2">
              <Form.Label>Date</Form.Label>
              <DatePicker
                value={expenseVal.date}
                onChange={e => setExpenseVal({ ...expenseVal, date: e.target.value })}
                autoComplete="off"
              />
            </Form.Group>
            <Form.Group className="mb-2">
              <Form.Label>Type of Expense</Form.Label>
              <ActivityExpenseSelector
                value={expenseVal.type}
                type="expense"
                onChange={e => setExpenseVal({ ...expenseVal, type: e.target.value })}
              />
            </Form.Group>
            <Form.Group className="mb-2">
              <Form.Label>Amount (Extra Expense)</Form.Label>
              <Form.Control
                type="number"
                step="0.01"
                disabled={isMileage}
                value={expenseVal.amount}
                onChange={e => setExpenseVal({ ...expenseVal, amount: parseFloat(e.target.value) || 0 })}
              />
            </Form.Group>
            <Form.Group className="mb-2">
              <Form.Label>Distance (Miles)</Form.Label>
              <Form.Control
                type="number"
                step="0.01"
                disabled={!isMileage}
                value={expenseVal.distance}
                onChange={e => setExpenseVal({ ...expenseVal, distance: parseFloat(e.target.value) || 0 })}
              />
            </Form.Group>
            <Form.Group className="mb-2">
              <Form.Label>Description</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                value={expenseVal.description}
                onChange={e => setExpenseVal({ ...expenseVal, description: e.target.value })}
              />
            </Form.Group>
          </div>
        )
      }
      case 'appointment-details': {
        const effectiveCaseNum = caseNumber || caseModel?.caseNumber || ''
        return (
          <div>
            <div className="d-flex justify-content-between align-items-center mb-3 pb-2 border-bottom">
              <span className="text-muted font-weight-bold">
                {appointmentVal.appointmentNumber
                  ? `Appointment #${appointmentVal.appointmentNumber}`
                  : 'Appointment Details'}
              </span>
              {effectiveCaseNum && appointmentVal.appointmentNumber && (
                <a
                  href={`/cases/${effectiveCaseNum}/appointments/${appointmentVal.appointmentNumber}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn btn-outline-primary btn-sm"
                  style={{ textDecoration: 'none' }}
                >
                  <i className="fas fa-external-link-alt mr-1" />
                  Open Full Appointment
                </a>
              )}
            </div>
            <Form.Group className="mb-2">
              <Form.Label className="font-weight-bold">Event Title</Form.Label>
              <Form.Control
                type="text"
                placeholder="e.g. PT assessment, MRI Review"
                value={appointmentVal.title}
                onChange={e => setAppointmentVal({ ...appointmentVal, title: e.target.value })}
              />
            </Form.Group>
            <Form.Group className="mb-2">
              <Form.Label className="font-weight-bold">Date</Form.Label>
              <DatePicker
                value={appointmentVal.date}
                onChange={e => setAppointmentVal({ ...appointmentVal, date: e.target.value })}
                autoComplete="off"
              />
            </Form.Group>
            <Form.Group className="mb-2">
              <Form.Label className="font-weight-bold">Provider</Form.Label>
              <Form.Control
                type="text"
                placeholder="Provider or Facility Name"
                value={appointmentVal.provider}
                onChange={e => setAppointmentVal({ ...appointmentVal, provider: e.target.value })}
              />
            </Form.Group>
            <Form.Group className="mb-2">
              <Form.Label className="font-weight-bold">Description</Form.Label>
              <Form.Control
                as="textarea"
                rows={2}
                placeholder="Appointment description..."
                value={appointmentVal.description}
                onChange={e => setAppointmentVal({ ...appointmentVal, description: e.target.value })}
              />
            </Form.Group>
            <Form.Group className="mb-2">
              <Form.Label className="font-weight-bold">Appointment Note</Form.Label>
              <TextArea
                className="form-control p-0 border-0"
                areaProps={{
                  value: appointmentVal.note,
                  onChange: val => setAppointmentVal({ ...appointmentVal, note: val }),
                }}
              />
            </Form.Group>
          </div>
        )
      }
      default:
        return null
    }
  }

  const handleDelete = () => {
    if (!isDeletingConfirm) {
      setIsDeletingConfirm(true)
    } else {
      if (type === 'activity') {
        onSave({ ...activityVal, _delete: true })
      } else {
        onSave({ ...expenseVal, _delete: true })
      }
      setIsDeletingConfirm(false)
    }
  }

  const handleEntered = () => {
    if (type === 'activity') {
      if (focusField === 'date') {
        const dateInput = document.querySelector('.modal-body .date-picker input')
        if (dateInput) {
          dateInput.focus()
          return
        }
      }
      if (descriptionInputRef.current) {
        descriptionInputRef.current.focus()
        return
      }
    }
    // Find the first input, textarea or contenteditable element inside the modal body
    const modalBody = document.querySelector('.modal-body')
    if (modalBody) {
      const firstInput = modalBody.querySelector('input, textarea, [contenteditable="true"], .ql-editor')
      if (firstInput) {
        firstInput.focus()
      }
    }
  }

  const serializeProvState = p =>
    `${p.id || p.providerId || ''}:${p.name || ''}:${p.parentOrganization || ''}:${p.companyName || ''}:${
      p._affiliationAction || ''
    }`
  const initialProviderIds = (Array.isArray(value) ? value : []).map(serializeProvState).join(',')
  const currentProviderIds = providersVal.map(serializeProvState).join(',')
  const hasProviderListChanges = initialProviderIds !== currentProviderIds

  return (
    <>
      <Modal
        show={show && !selectedEditProviderId}
        onHide={onHide}
        onEntered={handleEntered}
        enforceFocus={false}
        size={
          type === 'rich-text' ||
          type === 'injury-descriptions-list' ||
          type === 'address' ||
          type === 'systems-review' ||
          type === 'functional-statuses' ||
          type === 'mmi' ||
          type === 'appointment-details'
            ? 'lg'
            : 'md'
        }
      >
        <Modal.Header>
          <Modal.Title>{title || 'Field'}</Modal.Title>
          <button
            type="button"
            className="close"
            onClick={onHide}
            title="Cancel (discard changes)"
            style={{
              outline: 'none',
              background: 'transparent',
              border: 'none',
              fontSize: '1.5rem',
              fontWeight: '700',
              lineHeight: 1,
              color: '#000',
              opacity: '.5',
            }}
          >
            <span aria-hidden="true">&times;</span>
          </button>
        </Modal.Header>
        <Modal.Body>{renderEditor()}</Modal.Body>
        <Modal.Footer>
          {fieldKey === 'report.currentMedicalStatus' && (
            <Button
              variant="outline-primary"
              className="mr-auto"
              disabled={isGeneratingNarrative}
              onMouseDown={handleGenerateNarrative}
              onClick={handleGenerateNarrative}
            >
              {isGeneratingNarrative ? (
                <>
                  <span className="spinner-border spinner-border-sm mr-1" role="status" aria-hidden="true" />
                  Generating...
                </>
              ) : (
                <>
                  <FAIcon name="sparkles" className="mr-1" />
                  {textVal && textVal.trim() && textVal !== '<p></p>' ? 'Regenerate Narrative' : 'Generate Narrative'}
                </>
              )}
            </Button>
          )}
          {(type === 'activity' || type === 'expense') && fieldKey && !fieldKey.includes('.new.') && (
            <Button
              variant={isDeletingConfirm ? 'danger' : 'outline-danger'}
              className="mr-auto"
              onClick={handleDelete}
            >
              {isDeletingConfirm ? 'Confirm Delete?' : 'Delete'}
            </Button>
          )}
          {type === 'treatment-providers' ? (
            hasProviderListChanges ? (
              <>
                <Button
                  variant="secondary"
                  onMouseDown={e => {
                    e.preventDefault()
                    onHide()
                  }}
                  onClick={onHide}
                >
                  Cancel
                </Button>
                <Button variant="primary" onMouseDown={handleSave} onClick={handleSave}>
                  Save
                </Button>
              </>
            ) : (
              <Button
                variant="primary"
                onMouseDown={e => {
                  e.preventDefault()
                  onHide()
                }}
                onClick={onHide}
              >
                Close
              </Button>
            )
          ) : (
            <>
              <Button
                variant="secondary"
                onMouseDown={e => {
                  e.preventDefault()
                  onHide()
                }}
                onClick={onHide}
              >
                Cancel
              </Button>
              <Button variant="primary" onMouseDown={handleSave} onClick={handleSave}>
                Save
              </Button>
            </>
          )}
        </Modal.Footer>
      </Modal>

      {selectedEditProviderId && (
        <ProviderForm
          show={!!selectedEditProviderId}
          providerId={selectedEditProviderId}
          onHide={() => setSelectedEditProviderId(null)}
          onSuccess={updatedProvider => {
            if (updatedProvider) {
              setProvidersVal(prev =>
                prev.map(p => {
                  const pId = p.id || p.providerId
                  if (pId && String(pId).toLowerCase() === String(updatedProvider.id).toLowerCase()) {
                    const primaryAff =
                      updatedProvider.affiliations?.find(a => a.isPrimary) || updatedProvider.affiliations?.[0]
                    const affAddr = primaryAff?.address?.line1 ? primaryAff.address : null
                    const ownAddr =
                      updatedProvider.addresses?.find(a => a.type === 'PrimaryPractice' || a.isPrimary) ||
                      updatedProvider.addresses?.find(a => a.type === 'Mailing') ||
                      updatedProvider.addresses?.[0] ||
                      null
                    const primaryAddr = affAddr || ownAddr
                    const effectiveOrgName = primaryAff?.organizationName || updatedProvider.organizationName || ''
                    return {
                      ...p,
                      name: updatedProvider.name,
                      firstName: updatedProvider.firstName,
                      lastName: updatedProvider.lastName,
                      suffix: updatedProvider.suffix || '',
                      specialties: updatedProvider.specialties || '',
                      parentOrganization: effectiveOrgName || null,
                      companyName: effectiveOrgName || null,
                      phone: updatedProvider.phone || '',
                      fax: updatedProvider.fax || '',
                      email: updatedProvider.email || '',
                      address: primaryAddr,
                    }
                  }
                  return p
                }),
              )
              if (onRefresh) {
                onRefresh()
              }
            }
            setSelectedEditProviderId(null)
          }}
          initialType="Treatment"
        />
      )}
    </>
  )
}
